import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { RouteFallback } from "./components/ui/route-fallback";
import { AppShellLayout } from "./layouts/AppShellLayout";
import AuthedLayout from "./layouts/AuthedLayout";
import DemoLayout from "./layouts/DemoLayout";
import LocaleLayout from "./layouts/LocaleLayout";
import PublicActivitiesLayout from "./layouts/PublicActivitiesLayout";
import SyncLayout from "./layouts/SyncLayout";
import StravaPopupClose from "./pages/StravaPopupClose";

function lazyNamed<T extends Record<string, any>>(
  factory: () => Promise<T>,
  name: keyof T,
) {
  return React.lazy(() => factory().then((m) => ({ default: m[name] })));
}

const AuthPage = lazyNamed(() => import("./pages/AuthPage"), "AuthPage");
const CalendarPage = lazyNamed(
  () => import("./pages/CalendarPage"),
  "CalendarPage",
);
const ChartsPage = lazyNamed(() => import("./pages/ChartsPage"), "ChartsPage");
const Home = lazyNamed(() => import("./pages/HomePage"), "Home");
const MapPage = lazyNamed(() => import("./pages/MapPage"), "MapPage");
const PrivacyPage = lazyNamed(
  () => import("./pages/PrivacyPage"),
  "PrivacyPage",
);
const HelpPage = lazyNamed(() => import("./pages/HelpPage"), "HelpPage");
const FeedbackPage = lazyNamed(
  () => import("./pages/FeedbackPage"),
  "FeedbackPage",
);
const TablePage = lazyNamed(() => import("./pages/TablePage"), "TablePage");
const TermsPage = lazyNamed(() => import("./pages/TermsPage"), "TermsPage");

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public marketing + legal */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />

        {/* Authed app under /app */}
        <Route element={<AuthedLayout />}>
          <Route element={<SyncLayout />}>
            <Route element={<AppShellLayout />}>
              <Route
                path="/app"
                element={<Navigate to="/app/charts" replace />}
              />
              <Route path="/app/charts" element={<ChartsPage />} />
              <Route path="/app/table" element={<TablePage />} />
              <Route path="/app/calendar" element={<CalendarPage />} />
              <Route path="/app/map" element={<MapPage />} />

              <Route path="/heatmap" element={<PublicActivitiesLayout />}>
                <Route index element={<MapPage />} />
              </Route>

              <Route
                path="/app/*"
                element={<Navigate to="/app/charts" replace />}
              />
            </Route>
          </Route>
        </Route>

        {/* Demo under /demo */}
        <Route path="/demo" element={<DemoLayout />}>
          <Route element={<AppShellLayout />}>
            <Route path="" element={<Navigate to="charts" replace />} />
            <Route path="charts" element={<ChartsPage />} />
            <Route path="table" element={<TablePage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="map" element={<MapPage />} />
          </Route>
        </Route>

        <Route path="/strava/popup-close" element={<StravaPopupClose />} />

        {/* Prefixed locales */}
        <Route path="/:lang" element={<LocaleLayout />}>
          <Route index element={<Home />} />
          <Route path="home" element={<Home />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="feedback" element={<FeedbackPage />} />
        </Route>

        {/* Global catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
